/**
 * gsap-init.js — Mango Butter Brand
 * Registers ScrollTrigger, sets global defaults, and exposes
 * window.MB.gsap helpers for use in any section or snippet.
 *
 * Usage in a section's <script>:
 *   document.addEventListener('MB:gsap:ready', () => {
 *     MB.gsap.fadeUp('.my-element');
 *     MB.gsap.stagger('.card', { delay: 0 });
 *   });
 */

(function () {
  'use strict';

  // ── Wait for GSAP + ScrollTrigger to both be available ──────────────────
  function waitForGSAP(cb) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      cb();
    } else {
      setTimeout(() => waitForGSAP(cb), 50);
    }
  }

  waitForGSAP(function () {

    // ── 1. Register plugin ─────────────────────────────────────────────────
    gsap.registerPlugin(ScrollTrigger);

    // ── 2. Respect reduced motion ──────────────────────────────────────────
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      // Kill all animations — accessibility first
      gsap.globalTimeline.pause();
      ScrollTrigger.getAll().forEach(t => t.kill());
      return;
    }

    // ── 3. Global GSAP defaults ────────────────────────────────────────────
    gsap.defaults({
      ease: 'power3.out',
      duration: 0.9,
    });

    // ── 4. ScrollTrigger defaults ──────────────────────────────────────────
    ScrollTrigger.defaults({
      start: 'top 85%',
      toggleActions: 'play none none none',
    });

    // ── 5. Brand easing library ────────────────────────────────────────────
    const ease = {
      silk:   'cubic-bezier(0.25, 1, 0.5, 1)',
      spring: 'back.out(1.4)',
      soft:   'power2.out',
      hero:   'power4.out',
      out:    'power3.out',
    };

    // ── 6. MB.gsap helper library ──────────────────────────────────────────
    window.MB = window.MB || {};

    window.MB.gsap = {

      /**
       * fadeUp — fade + translateY entrance on scroll
       * @param {string|Element} target  — CSS selector or DOM element
       * @param {object}         opts    — overrides: { delay, duration, y, stagger, ease, start }
       */
      fadeUp(target, opts = {}) {
        const els = gsap.utils.toArray(target);
        if (!els.length) return;

        return gsap.from(els, {
          y:       opts.y        ?? 40,
          opacity: 0,
          duration:opts.duration ?? 1,
          delay:   opts.delay    ?? 0,
          stagger: opts.stagger  ?? 0,
          ease:    opts.ease     ?? ease.out,
          scrollTrigger: {
            trigger: els[0],
            start:   opts.start  ?? 'top 85%',
          },
        });
      },

      /**
       * stagger — staggered fadeUp for grids / lists
       * @param {string} target  — CSS selector for the items
       * @param {object} opts    — overrides
       */
      stagger(target, opts = {}) {
        const els = gsap.utils.toArray(target);
        if (!els.length) return;

        return gsap.from(els, {
          y:       opts.y        ?? 32,
          opacity: 0,
          duration:opts.duration ?? 0.8,
          stagger: opts.stagger  ?? 0.1,
          delay:   opts.delay    ?? 0,
          ease:    opts.ease     ?? ease.out,
          scrollTrigger: {
            trigger: opts.trigger ?? els[0].parentElement ?? els[0],
            start:   opts.start   ?? 'top 80%',
          },
        });
      },

      /**
       * revealText — clip-path word/line reveal (hero headings)
       * @param {string} target   — CSS selector (wrapping element)
       * @param {object} opts
       */
      revealText(target, opts = {}) {
        const els = gsap.utils.toArray(target);
        if (!els.length) return;

        return gsap.from(els, {
          y:          opts.y         ?? '110%',
          opacity:    opts.opacity   ?? 0,
          duration:   opts.duration  ?? 1.1,
          stagger:    opts.stagger   ?? 0.08,
          ease:       opts.ease      ?? ease.hero,
          delay:      opts.delay     ?? 0,
          clipPath:   opts.clip ? 'inset(0 0 100% 0)' : undefined,
          scrollTrigger: opts.scroll ? {
            trigger: els[0],
            start:  'top 90%',
          } : undefined,
        });
      },

      /**
       * parallax — vertical parallax on scroll
       * @param {string} target
       * @param {object} opts  — { speed: 0.3, start, end }
       */
      parallax(target, opts = {}) {
        const els = gsap.utils.toArray(target);
        if (!els.length) return;

        const speed = opts.speed ?? 0.3;

        return els.map(el => gsap.to(el, {
          yPercent: -100 * speed,
          ease: 'none',
          scrollTrigger: {
            trigger:    opts.trigger ?? el,
            start:      opts.start   ?? 'top bottom',
            end:        opts.end     ?? 'bottom top',
            scrub:      opts.scrub   ?? true,
          },
        }));
      },

      /**
       * pinSection — pin a section while scrolling through content
       * @param {string} pin       — element to pin
       * @param {string} trigger   — scroll trigger container
       * @param {object} opts
       */
      pinSection(pin, trigger, opts = {}) {
        return ScrollTrigger.create({
          trigger: trigger,
          start:   opts.start ?? 'top top',
          end:     opts.end   ?? 'bottom bottom',
          pin:     pin,
          pinSpacing: opts.pinSpacing ?? false,
          scrub:   opts.scrub ?? true,
        });
      },

      /**
       * marquee — infinite horizontal scroll for text/logo strips
       * @param {string} track     — the inner track element
       * @param {object} opts      — { speed: 60, direction: -1 }
       */
      marquee(track, opts = {}) {
        const el = document.querySelector(track);
        if (!el) return;

        const speed     = opts.speed     ?? 60; // px/sec
        const direction = opts.direction ?? -1; // -1 = left

        const tl = gsap.timeline({ repeat: -1, defaults: { ease: 'none' } });
        const width = el.scrollWidth / 2; // assumes content duplicated

        tl.to(el, {
          x: direction * width,
          duration: width / speed,
        });

        return tl;
      },

      /**
       * drawLine — SVG stroke draw-on effect
       * @param {string} target    — SVG path/line selector
       * @param {object} opts
       */
      drawLine(target, opts = {}) {
        const els = gsap.utils.toArray(target);
        if (!els.length) return;

        return gsap.from(els, {
          strokeDashoffset: opts.offset ?? 1000,
          duration:  opts.duration ?? 1.5,
          ease:      ease.silk,
          scrollTrigger: {
            trigger: els[0],
            start:  opts.start ?? 'top 80%',
          },
        });
      },

      /**
       * floatLoop — endless gentle float (hero product / glass elements)
       * @param {string} target
       * @param {object} opts  — { y: 16, duration: 4, delay: 0 }
       */
      floatLoop(target, opts = {}) {
        const els = gsap.utils.toArray(target);
        if (!els.length) return;

        return els.map((el, i) => gsap.to(el, {
          y:        opts.y        ?? 16,
          duration: opts.duration ?? 4,
          delay:    (opts.delay ?? 0) + i * 0.4,
          ease:     'sine.inOut',
          yoyo:     true,
          repeat:   -1,
        }));
      },

      /**
       * scaleIn — scale + fade entrance (modals, badges, dropdowns)
       * @param {string} target
       * @param {object} opts
       */
      scaleIn(target, opts = {}) {
        return gsap.from(target, {
          scale:   opts.scale   ?? 0.92,
          opacity: 0,
          duration:opts.duration ?? 0.4,
          ease:    ease.spring,
          delay:   opts.delay    ?? 0,
        });
      },

      /**
       * drawerIn — slide-in from right (cart drawer, mobile menu)
       * @param {string} target
       * @param {function} onComplete
       */
      drawerIn(target, onComplete) {
        return gsap.from(target, {
          x:        '100%',
          duration: 0.45,
          ease:     ease.silk,
          onComplete,
        });
      },

      drawerOut(target, onComplete) {
        return gsap.to(target, {
          x:        '100%',
          duration: 0.32,
          ease:     'power2.in',
          onComplete,
        });
      },

      /**
       * counterUp — animated number counter
       * @param {string} target    — element containing a number
       * @param {object} opts      — { from: 0, to: 200, duration: 2, prefix: '', suffix: '+' }
       */
      counterUp(target, opts = {}) {
        const el = document.querySelector(target);
        if (!el) return;

        const obj = { val: opts.from ?? 0 };
        const to  = opts.to ?? parseInt(el.textContent, 10) ?? 100;

        return gsap.to(obj, {
          val:      to,
          duration: opts.duration ?? 2,
          ease:     'power2.out',
          onUpdate() {
            el.textContent =
              (opts.prefix ?? '') +
              Math.round(obj.val).toLocaleString() +
              (opts.suffix ?? '');
          },
          scrollTrigger: {
            trigger: el,
            start:  'top 85%',
            once:   true,
          },
        });
      },

      // ── Utility: refresh ScrollTrigger (call after DOM changes) ─────────
      refresh() {
        ScrollTrigger.refresh();
      },

      // ── Utility: kill all ScrollTriggers (use in section:unload) ────────
      killAll() {
        ScrollTrigger.getAll().forEach(t => t.kill());
      },

    }; // end MB.gsap

    // ── 7. Auto-init data-gsap attribute elements ──────────────────────────
    // Use data-gsap="fade-up" | "stagger" | "parallax" | "float" on any element
    // without writing JavaScript in sections.
    function autoInit() {
      // fade-up
      document.querySelectorAll('[data-gsap="fade-up"]').forEach(el => {
        const delay = parseFloat(el.dataset.gsapDelay ?? 0);
        const y     = parseFloat(el.dataset.gsapY ?? 40);
        MB.gsap.fadeUp(el, { delay, y });
      });

      // stagger — targets the children
      document.querySelectorAll('[data-gsap="stagger"]').forEach(parent => {
        const children = parent.dataset.gsapTarget
          ? parent.querySelectorAll(parent.dataset.gsapTarget)
          : parent.children;
        MB.gsap.stagger([...children], {
          trigger: parent,
          stagger: parseFloat(parent.dataset.gsapStagger ?? 0.1),
        });
      });

      // parallax
      document.querySelectorAll('[data-gsap="parallax"]').forEach(el => {
        MB.gsap.parallax(el, {
          speed: parseFloat(el.dataset.gsapSpeed ?? 0.25),
        });
      });

      // float
      document.querySelectorAll('[data-gsap="float"]').forEach(el => {
        MB.gsap.floatLoop(el, {
          y:        parseFloat(el.dataset.gsapY ?? 16),
          duration: parseFloat(el.dataset.gsapDuration ?? 4),
          delay:    parseFloat(el.dataset.gsapDelay ?? 0),
        });
      });
    }

    // Run once DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', autoInit);
    } else {
      autoInit();
    }

    // Re-run after Shopify section re-renders (Theme Editor)
    document.addEventListener('shopify:section:load', function () {
      autoInit();
      ScrollTrigger.refresh();
    });

    document.addEventListener('shopify:section:unload', function () {
      ScrollTrigger.refresh();
    });

    // ── 8. Dispatch ready event ────────────────────────────────────────────
    document.dispatchEvent(new CustomEvent('MB:gsap:ready', { bubbles: true }));
    console.log('[MB] GSAP + ScrollTrigger ready ✓');

  }); // end waitForGSAP

})();
